import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { authApi } from '@/api/resources/auth'
import { rememberWorkspaceSlug } from '@/lib/workspace'
import { type AuthenticatedSession, type Session } from '@/api/schemas/session'
import { hasAnyRole, type Role } from './roles'

export const SESSION_QUERY_KEY = ['auth', 'session'] as const

/** Refresh the BFF session when less than this long remains (§5.1 keep-alive). */
const KEEP_ALIVE_THRESHOLD_MS = 5 * 60 * 1000

export interface SessionContextValue {
  /** Raw session state; undefined while the bootstrap request is in flight. */
  session: Session | undefined
  /** The signed-in identity, or null when unauthenticated / still loading. */
  user: AuthenticatedSession | null
  isLoading: boolean
  hasRole: (...roles: Role[]) => boolean
  /** Force a session refetch (e.g. after impersonation starts/ends). */
  reload: () => Promise<void>
  /** Sign out: clears BFF + Keycloak session, then hard-redirects to "/". */
  signOut: () => Promise<void>
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const { data: session, isLoading } = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: authApi.getSession,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: 1,
    // Never toast the bootstrap: 401 already resolves to signed-out inside
    // getSession, and a transient failure renders as signed-out UI, not a card.
    meta: { silentError: true },
  })

  const user = session?.authenticated ? session : null

  // Keep the remembered workspace fresh so re-auth and session-expired
  // redirects can supply the tenantSlug the hosted-login endpoint requires.
  useEffect(() => {
    if (user?.tenantSlug) rememberWorkspaceSlug(user.tenantSlug)
  }, [user])

  // Keep-alive: silently refresh shortly before the access token expires,
  // but only while the tab is visible.
  useEffect(() => {
    if (!user) return
    const expiresIn = new Date(user.accessTokenExpiresAt).getTime() - Date.now()
    const delay = Math.max(expiresIn - KEEP_ALIVE_THRESHOLD_MS, 5_000)
    const timer = window.setTimeout(() => {
      if (document.visibilityState !== 'visible') return
      void authApi
        .refresh()
        .then(() => queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY }))
        .catch(() => queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY }))
    }, delay)
    return () => window.clearTimeout(timer)
  }, [user, queryClient])

  const value = useMemo<SessionContextValue>(
    () => ({
      session,
      user,
      isLoading,
      hasRole: (...roles) => hasAnyRole(user, roles),
      reload: async () => {
        await queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY })
      },
      signOut: async () => {
        try {
          await authApi.logout()
        } finally {
          window.location.assign('/')
        }
      },
    }),
    [session, user, isLoading, queryClient],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext)
  if (!context) throw new Error('useSession must be used within <SessionProvider>')
  return context
}

/** For screens guarded by RequireAuth, where the user is guaranteed present. */
export function useCurrentUser(): AuthenticatedSession {
  const { user } = useSession()
  if (!user) throw new Error('useCurrentUser used outside an authenticated route')
  return user
}
